import { useCallback, useState } from "react";
import { Layout } from "@/components/Layout";
import {
    createJourney, readJourney, evaluateJourney, repairJourney,
    reevaluateJourney, executeAdapter,
    createLiveElection, readLiveElection, evaluateLiveElection,
    repairLiveElection, reevaluateLiveElection, readElectionReceipt,
} from "@/lib/api";

/*
 * Live Engine workbench. Every state, rule result, event, authorisation and
 * adapter verdict shown here is returned by the authoritative backend. This
 * page initiates commands and renders responses; it computes nothing.
 */

const FLOWS = [
    {
        key: "bond-freeze",
        label: "Bond transfer - freeze & repair",
        kind: "transfer",
        create: () => createJourney({ instruction_id: "TX-BOND-002" }),
        blurb: "A mandatory structured settlement field is missing. The engine discovers it at runtime (rule HLI-R5, the ISO 20022 November 2026 rule), freezes the transfer and assigns repair to the Buyer Custodian.",
        repairRole: "Buyer Custodian",
        wrongRole: "Registrar",
        repairBody: (view) => ({
            expected_version: view.version,
            correlation_id: view.open_repair_request.correlation_id,
            field: "counterparty_account_reference",
            value: "SETT-ACCT-NQI-0555",
            evidence_reference: {
                label: "Corrected settlement context record",
                hash: "sha256:feedc0de…5555",
            },
        }),
    },
    {
        key: "bond-clean",
        label: "Bond transfer - clean",
        kind: "transfer",
        create: () => createJourney({ instruction_id: "TX-BOND-001" }),
        blurb: "All controls pass. The engine issues a scoped, expiring, one-time authorisation the adapter verifies and consumes.",
    },
    {
        key: "fund-clean",
        label: "Fund unit - same engine",
        kind: "transfer",
        create: () => createJourney({ instruction_id: "TX-FUND-001" }),
        blurb: "The identical engine evaluates the fund policy pack MSIF-SUB-01. Different rules, no different code path.",
    },
    {
        key: "fund-deny",
        label: "Fund unit - deny",
        kind: "transfer",
        create: () => createJourney({ instruction_id: "TX-FUND-002" }),
        blurb: "A retail investor profile fails the fund's professional-investor rule. The engine denies; the adapter never receives an authorisation.",
    },
    {
        key: "election-repair",
        label: "CA election - mandate repair",
        kind: "election",
        create: () => createLiveElection({
            parent_event_ref: "CAEV-HARB-2026-001", option_ref: "OPT-A",
            mandate_present: false,
        }),
        blurb: "A voluntary early-redemption election binds to parent event CAEV-HARB-2026-001, option OPT-A. The mandate evidence reference is missing: FREEZE, repair by the Holder Custodian, RELEASE, one-time adapter consumption. Role nodes assert consensus over the envelope state hash at every step.",
        repairRole: "Holder Custodian",
        wrongRole: "Registrar",
        repairBody: (view) => ({
            expected_version: view.version,
            correlation_id: view.open_repair_request.correlation_id,
            evidence_type: "mandate_reference",
            evidence_reference: {
                label: "Election mandate reference (renewed)",
                hash: "sha256:0a1b2c3d…mnd2",
            },
        }),
    },
    {
        key: "election-clean",
        label: "CA election - clean",
        kind: "election",
        create: () => createLiveElection({
            parent_event_ref: "CAEV-HARB-2026-001", option_ref: "OPT-A",
            mandate_present: true,
        }),
        blurb: "All election conditions pass: the adapter is authorised to lock the position, mark the register and emit the notification - exactly once.",
    },
];

const STAGES = [
    { key: "instruction", label: "Instruction" },
    { key: "evaluate", label: "Evaluate" },
    { key: "repair", label: "R2R repair" },
    { key: "reevaluate", label: "Re-evaluate" },
    { key: "adapter", label: "Adapter" },
    { key: "receipt", label: "Receipt" },
];

const OUTCOME_STYLES = {
    ALLOW: "bg-emerald-50 text-emerald-800 border-emerald-200",
    RELEASE: "bg-emerald-50 text-emerald-800 border-emerald-200",
    FREEZE: "bg-amber-50 text-amber-800 border-amber-200",
    REVIEW: "bg-amber-50 text-amber-800 border-amber-200",
    DENY: "bg-red-50 text-red-800 border-red-200",
};

function Badge({ value }) {
    const cls = OUTCOME_STYLES[value] || "bg-slate-50 text-slate-700 border-slate-200";
    return (
        <span className={`inline-block rounded-sm border px-2 py-0.5 font-mono text-xs ${cls}`}>
            {value}
        </span>
    );
}

function Card({ title, children, tone = "white", testid }) {
    return (
        <section
            data-testid={testid}
            className={`rounded-sm border p-5 ${
                tone === "reject"
                    ? "border-red-200 bg-red-50"
                    : tone === "ok"
                    ? "border-emerald-200 bg-emerald-50"
                    : "border-slate-200 bg-white"
            }`}
        >
            <h3 className="text-sm font-medium text-slate-900">{title}</h3>
            <div className="mt-3">{children}</div>
        </section>
    );
}

export default function LiveEngine() {
    const [flow, setFlow] = useState(FLOWS[0]);
    const [view, setView] = useState(null);
    const [sessions, setSessions] = useState(null);
    const [auth, setAuth] = useState(null);
    const [verdicts, setVerdicts] = useState([]);
    const [rejection, setRejection] = useState(null);
    const [receipt, setReceipt] = useState(null);
    const [busy, setBusy] = useState(false);

    const isElection = flow.kind === "election";
    const read = isElection ? readLiveElection : readJourney;
    const evaluate = isElection ? evaluateLiveElection : evaluateJourney;
    const repair = isElection ? repairLiveElection : repairJourney;
    const reevaluate = isElection ? reevaluateLiveElection : reevaluateJourney;

    const refresh = useCallback(async (id) => {
        const v = await read(id);
        setView(v);
        return v;
    }, [read]);

    const selectFlow = (f) => {
        setFlow(f);
        setView(null); setSessions(null); setAuth(null);
        setVerdicts([]); setRejection(null); setReceipt(null);
    };

    const run = async (fn) => {
        setBusy(true);
        setRejection(null);
        try {
            await fn();
        } catch (err) {
            const detail = err?.response?.data?.detail || String(err);
            const status = err?.response?.status;
            setRejection({ status, detail });
            if (view) await refresh(view.journey_id);
        } finally {
            setBusy(false);
        }
    };

    const start = () => run(async () => {
        const created = await flow.create();
        setSessions(created.synthetic_role_sessions);
        setAuth(null); setVerdicts([]); setReceipt(null);
        await (async () => setView(created))();
        await refresh(created.journey_id);
    });

    const doEvaluate = () => run(async () => {
        const r = await evaluate(view.journey_id, view.version);
        if (r.authorisation) setAuth(r.authorisation);
        await refresh(view.journey_id);
    });

    const doRepair = (asWrongRole) => run(async () => {
        const body = flow.repairBody(view);
        const role = asWrongRole ? flow.wrongRole : flow.repairRole;
        await repair(view.journey_id, body, sessions[role]);
        await refresh(view.journey_id);
    });

    const doReevaluate = () => run(async () => {
        const r = await reevaluate(view.journey_id, view.version);
        if (r.authorisation) setAuth(r.authorisation);
        await refresh(view.journey_id);
    });

    const doExecute = (which) => run(async () => {
        let presented = auth;
        if (which === "forged") presented = { ...auth, outcome: "RELEASE", scope_note: "tampered" };
        const verdict = await executeAdapter(view.journey_id, presented);
        setVerdicts((v) => [...v, { which: which || "valid", verdict }]);
        const fresh = await refresh(view.journey_id);
        if (isElection && fresh.state === "EXECUTED") {
            setReceipt(await readElectionReceipt(view.journey_id));
        }
    });

    const state = view?.state;
    const stageStatus = (key) => {
        if (!view) return "todo";
        const evalDone = view.latest_evaluation != null;
        const repaired = view.events?.some((e) => e.type === "EvidenceReferenceUpdated");
        const reevaluated = view.events?.some((e) => e.type === "ControlReevaluated");
        const executed = state === "EXECUTED";
        switch (key) {
            case "instruction": return "done";
            case "evaluate": return evalDone ? "done" : "next";
            case "repair":
                if (repaired) return "done";
                return state === "REPAIR_PENDING" ? "next" : "todo";
            case "reevaluate":
                if (reevaluated) return "done";
                return state === "REPAIRED" ? "next" : "todo";
            case "adapter":
                if (executed) return "done";
                return state === "ALLOWED" || state === "RELEASED" ? "next" : "todo";
            case "receipt": return executed ? "done" : "todo";
            default: return "todo";
        }
    };

    const evaluation = view?.latest_evaluation;

    return (
        <Layout wide>
            <p className="text-xs font-mono uppercase tracking-[0.18em] text-slate-400">
                Authoritative backend · Nothing on this page is precomputed
            </p>
            <h1 className="mt-3 text-3xl font-medium tracking-tight text-slate-900">
                Live engine workbench
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-slate-600">
                Every decision below is calculated at runtime by the governance
                backend from the instruction, role context, evidence references and
                the selected policy pack. This page initiates commands and renders
                the backend's responses; it never computes an outcome.
            </p>

            {/* Flow selector */}
            <div className="mt-8 flex flex-wrap gap-2">
                {FLOWS.map((f) => (
                    <button
                        key={f.key}
                        onClick={() => selectFlow(f)}
                        data-testid={`flow-${f.key}`}
                        className={`rounded-sm border px-3 py-1.5 text-sm transition-colors ${
                            flow.key === f.key
                                ? "border-blue-950 bg-blue-950 text-white"
                                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                        }`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-500">{flow.blurb}</p>

            {/* Stage rail */}
            <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="stage-rail">
                {STAGES.map((s, i) => {
                    const st = stageStatus(s.key);
                    return (
                        <div key={s.key} className="flex items-center gap-2">
                            <span
                                className={`rounded-sm px-2.5 py-1 font-mono text-xs ${
                                    st === "done"
                                        ? "bg-blue-950 text-white"
                                        : st === "next"
                                        ? "border border-blue-950 bg-white text-blue-950"
                                        : "border border-slate-200 bg-white text-slate-400"
                                }`}
                            >
                                {s.label}
                            </span>
                            {i < STAGES.length - 1 && <span className="text-slate-300">→</span>}
                        </div>
                    );
                })}
                {view && (
                    <span className="ml-auto font-mono text-xs text-slate-500">
                        {view.journey_id} · state {state} · v{view.version}
                    </span>
                )}
            </div>

            {/* Actions */}
            <div className="mt-5 flex flex-wrap gap-2">
                <button onClick={start} disabled={busy} data-testid="btn-start"
                    className="rounded-sm bg-blue-950 px-4 py-2 text-sm font-medium text-white hover:bg-blue-900 disabled:opacity-50">
                    {view ? "Restart scenario" : "Start scenario"}
                </button>
                {view && state === "RECEIVED" && (
                    <button onClick={doEvaluate} disabled={busy} data-testid="btn-evaluate"
                        className="rounded-sm bg-blue-950 px-4 py-2 text-sm font-medium text-white hover:bg-blue-900 disabled:opacity-50">
                        Evaluate control
                    </button>
                )}
                {view && state === "REPAIR_PENDING" && (
                    <>
                        <button onClick={() => doRepair(false)} disabled={busy} data-testid="btn-repair"
                            className="rounded-sm bg-blue-950 px-4 py-2 text-sm font-medium text-white hover:bg-blue-900 disabled:opacity-50">
                            Submit repair as {flow.repairRole}
                        </button>
                        <button onClick={() => doRepair(true)} disabled={busy} data-testid="btn-repair-wrong"
                            className="rounded-sm border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">
                            Adversarial: repair as {flow.wrongRole}
                        </button>
                    </>
                )}
                {view && state === "REPAIRED" && (
                    <button onClick={doReevaluate} disabled={busy} data-testid="btn-reevaluate"
                        className="rounded-sm bg-blue-950 px-4 py-2 text-sm font-medium text-white hover:bg-blue-900 disabled:opacity-50">
                        Re-evaluate
                    </button>
                )}
                {view && (state === "ALLOWED" || state === "RELEASED") && auth && (
                    <>
                        <button onClick={() => doExecute()} disabled={busy} data-testid="btn-execute"
                            className="rounded-sm bg-blue-950 px-4 py-2 text-sm font-medium text-white hover:bg-blue-900 disabled:opacity-50">
                            Present authorisation to adapter
                        </button>
                        <button onClick={() => doExecute("forged")} disabled={busy} data-testid="btn-forge"
                            className="rounded-sm border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">
                            Adversarial: present forged authorisation
                        </button>
                    </>
                )}
                {view && state === "EXECUTED" && auth && (
                    <button onClick={() => doExecute("replay")} disabled={busy} data-testid="btn-replay"
                        className="rounded-sm border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">
                        Adversarial: replay the consumed authorisation
                    </button>
                )}
            </div>

            {/* Governed rejection banner */}
            {rejection && (
                <div className="mt-4 rounded-sm border border-red-200 bg-red-50 p-4" data-testid="rejection-banner">
                    <p className="font-mono text-xs uppercase tracking-wide text-red-500">
                        Governed rejection · HTTP {rejection.status} · no state mutated
                    </p>
                    <p className="mt-1 text-sm text-red-800">{rejection.detail}</p>
                </div>
            )}

            {/* Panels */}
            {view && (
                <div className="mt-8 grid gap-4 lg:grid-cols-2">
                    {evaluation && (
                        <Card title={`Rule trace · ${evaluation.policy_id} v${evaluation.policy_version} · engine outcome:`} testid="panel-rules">
                            <div className="mb-3"><Badge value={evaluation.outcome} /></div>
                            <table className="w-full text-sm">
                                <tbody>
                                    {evaluation.rule_results.map((r) => (
                                        <tr key={r.rule_id} className="border-b border-slate-100 last:border-0">
                                            <td className="py-1.5 pr-2 font-mono text-xs text-slate-500">{r.rule_id}</td>
                                            <td className="py-1.5 pr-2 text-slate-700">{r.name}</td>
                                            <td className={`py-1.5 text-right font-mono text-xs ${r.result === "Pass" ? "text-emerald-700" : "text-red-700"}`}>
                                                {r.result}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Card>
                    )}

                    {view.open_repair_request && (
                        <Card title="Open R2R repair request" testid="panel-repair">
                            <dl className="space-y-1.5 text-sm text-slate-700">
                                <div><span className="font-mono text-xs text-slate-400">FROM </span>{view.open_repair_request.sender_role}</div>
                                <div><span className="font-mono text-xs text-slate-400">TO </span>{view.open_repair_request.recipient_role} · {view.open_repair_request.recipient_institution}</div>
                                <div><span className="font-mono text-xs text-slate-400">PURPOSE </span>{view.open_repair_request.purpose}</div>
                                <div><span className="font-mono text-xs text-slate-400">FAILED </span>{view.open_repair_request.failed_rules.map((r) => r.rule_id).join(", ")}</div>
                                <div><span className="font-mono text-xs text-slate-400">CORRELATION </span><span className="font-mono text-xs">{view.open_repair_request.correlation_id}</span></div>
                                <div><span className="font-mono text-xs text-slate-400">EXPIRES </span><span className="font-mono text-xs">{view.open_repair_request.expires_at}</span></div>
                            </dl>
                        </Card>
                    )}

                    {auth && (
                        <Card title="Execution authorisation (as presented to the adapter)" testid="panel-auth">
                            <dl className="space-y-1.5 text-sm text-slate-700">
                                <div><span className="font-mono text-xs text-slate-400">ID </span><span className="font-mono text-xs">{auth.authorisation_id}</span></div>
                                <div><span className="font-mono text-xs text-slate-400">OUTCOME </span><Badge value={auth.outcome} /></div>
                                <div><span className="font-mono text-xs text-slate-400">SCOPE </span>{auth.transfer_event_id} · {auth.asset_profile} · {auth.requested_action}</div>
                                <div><span className="font-mono text-xs text-slate-400">POLICY </span>{auth.policy_id} v{auth.policy_version} · control v{auth.control_version}</div>
                                <div><span className="font-mono text-xs text-slate-400">EXPIRES </span><span className="font-mono text-xs">{auth.expires_at}</span></div>
                                <div><span className="font-mono text-xs text-slate-400">NONCE </span><span className="font-mono text-xs">{auth.nonce.slice(0, 22)}…</span></div>
                                <div><span className="font-mono text-xs text-slate-400">SEAL </span><span className="font-mono text-xs">{auth.integrity_scheme} · {auth.integrity_digest.slice(0, 18)}…</span></div>
                                <div><span className="font-mono text-xs text-slate-400">KATLAS </span>{auth.katlas_attestation.state}{auth.katlas_attestation.live_signed ? " · live-signed" : " · honest state, nothing fabricated"}</div>
                            </dl>
                        </Card>
                    )}

                    {verdicts.map((v, i) => (
                        <Card
                            key={i}
                            title={`Adapter verdict · ${v.which === "valid" ? "presented authorisation" : `adversarial: ${v.which}`}`}
                            tone={v.verdict.accepted ? "ok" : "reject"}
                            testid={`panel-verdict-${i}`}
                        >
                            <p className="text-sm font-medium">
                                {v.verdict.accepted ? "ACCEPTED" : "REJECTED"} · <span className="font-mono text-xs">{v.verdict.reason_code}</span>
                            </p>
                            <p className="mt-1 text-sm text-slate-700">{v.verdict.message}</p>
                            <ul className="mt-3 space-y-1">
                                {v.verdict.checks.map((c, k) => (
                                    <li key={k} className="flex justify-between gap-3 text-xs">
                                        <span className="font-mono text-slate-500">{c.check}</span>
                                        <span className={`font-mono ${c.result === "Pass" ? "text-emerald-700" : "text-red-700"}`}>{c.result}</span>
                                    </li>
                                ))}
                            </ul>
                        </Card>
                    ))}

                    {receipt && (
                        <Card title="Receipt finality (public-safe projection)" tone="ok" testid="panel-receipt">
                            <dl className="space-y-1.5 text-sm text-slate-700">
                                <div><span className="font-mono text-xs text-slate-400">PARENT </span>{receipt.parent_event_ref} · {receipt.option_ref}</div>
                                <div><span className="font-mono text-xs text-slate-400">OUTCOME </span><Badge value={receipt.outcome} /> · executed: {String(receipt.executed)}</div>
                                <div><span className="font-mono text-xs text-slate-400">CONSENSUS </span>{String(receipt.consensus_reached)} · repair occurred: {String(receipt.governed_repair_occurred)}</div>
                                <div><span className="font-mono text-xs text-slate-400">STATE HASH </span><span className="font-mono text-xs">{receipt.envelope_state_hash.slice(0, 34)}…</span></div>
                            </dl>
                            <p className="mt-3 text-xs leading-relaxed text-slate-500">{receipt.withheld_detail_note}</p>
                        </Card>
                    )}

                    <Card title={`Event chain · ${view.events.length} role-attributed events`} testid="panel-events">
                        <ol className="space-y-2">
                            {view.events.map((e) => (
                                <li key={e.event_id} className="rounded-sm border border-slate-100 p-2.5 text-sm">
                                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                                        <span className="font-medium text-slate-800">{e.type}</span>
                                        <span className="font-mono text-xs text-slate-400">
                                            {e.previous_state} → {e.resulting_state}
                                        </span>
                                    </div>
                                    <div className="mt-0.5 font-mono text-xs text-slate-500">
                                        {e.acting_role} · {e.acting_institution}
                                    </div>
                                    {e.consensus && (
                                        <div className="mt-1 font-mono text-[11px] text-slate-400">
                                            consensus {e.consensus.consensus_reached ? "✓" : "✗"} · {e.consensus.assertions.map((a) => a.role_node).join(" + ")} · {e.consensus.state_hash.slice(0, 22)}…
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ol>
                    </Card>
                </div>
            )}
        </Layout>
    );
}
